<template>
  <el-drawer
    :title="title"
    :visible.sync="drawerVisible"
    direction="rtl"
    size="88%"
    append-to-body
    :wrapper-closable="true"
    custom-class="report-detail-drawer"
    @closed="$emit('closed')"
  >
    <div class="report-detail-body" v-loading="loading">
      <div class="report-detail-scroll">
        <div v-if="detail" class="report-detail-info">
          <el-descriptions :column="3" border size="small">
            <el-descriptions-item v-for="item in summaryItems" :key="item.label" :label="item.label">
              <template v-if="item.kind === 'status'">
                <gas-status-tag kind="reportRecord" :value="item.value" />
              </template>
              <template v-else>{{ item.value }}</template>
            </el-descriptions-item>
          </el-descriptions>
        </div>

        <div class="report-detail-title-row">
          <span class="table-title">{{ tableTitle }} <em>共 {{ total }} 条记录</em></span>
          <el-button type="success" size="small" icon="el-icon-download" :disabled="!detail" @click="$emit('export')">
            {{ exportLabel }}
          </el-button>
        </div>

        <el-table
          :data="rows"
          v-loading="rowsLoading"
          border
          size="small"
          :height="tableHeight"
          class="report-detail-table"
          header-cell-class-name="report-detail-table-header"
        >
          <el-table-column label="序号" width="60" align="center">
            <template slot-scope="{ $index }">{{ rowIndex($index) }}</template>
          </el-table-column>

          <template v-if="mode === 'yearly' && !typedMetrics.length">
            <el-table-column prop="monthLabel" label="统计月份" width="110" align="center" />
            <el-table-column prop="dayCount" label="上报天数" width="90" align="center" />
            <el-table-column label="月标况累积(m³)" min-width="130" align="right">
              <template slot-scope="{ row }">{{ formatNum(row.stdTotalSum) }}</template>
            </el-table-column>
            <el-table-column label="月均瞬时(m³/h)" min-width="130" align="right">
              <template slot-scope="{ row }">{{ formatNum(row.stdInstantAvg) }}</template>
            </el-table-column>
            <el-table-column label="月工况累积(m³)" min-width="130" align="right">
              <template slot-scope="{ row }">{{ formatNum(row.workTotalSum) }}</template>
            </el-table-column>
            <el-table-column label="月均压力(MPa)" width="120" align="right">
              <template slot-scope="{ row }">{{ formatNum(row.pressureAvg) }}</template>
            </el-table-column>
            <el-table-column prop="abnormalDays" label="异常天数" width="90" align="center" />
          </template>

          <template v-else-if="typedMetrics.length">
            <el-table-column v-if="mode === 'yearly'" prop="monthLabel" label="统计月份" width="110" align="center" />
            <el-table-column
              v-else
              :label="hasSampleTime ? '采样时间' : '统计日期'"
              :width="hasSampleTime ? 160 : 110"
              align="center"
            >
              <template slot-scope="{ row }">{{ periodLabel(row) }}</template>
            </el-table-column>
            <el-table-column
              v-for="m in typedMetrics"
              :key="m.key"
              :label="metricColumnLabel(m)"
              :min-width="m.width || 120"
              align="right"
            >
              <template slot-scope="{ row }">{{ formatNum(metricValue(row, m)) }}</template>
            </el-table-column>
          </template>

          <template v-else>
            <el-table-column
              :label="hasSampleTime ? '采样时间' : '统计日期'"
              :width="hasSampleTime ? 160 : 110"
              align="center"
            >
              <template slot-scope="{ row }">{{ periodLabel(row) }}</template>
            </el-table-column>
            <el-table-column label="标况累积(m³)" min-width="120" align="right">
              <template slot-scope="{ row }">{{ formatNum(row.stdTotal) }}</template>
            </el-table-column>
            <el-table-column label="标况瞬时(m³/h)" min-width="130" align="right">
              <template slot-scope="{ row }">{{ formatNum(row.stdInstant) }}</template>
            </el-table-column>
            <el-table-column label="工况累积(m³)" min-width="120" align="right">
              <template slot-scope="{ row }">{{ formatNum(row.workTotal) }}</template>
            </el-table-column>
            <el-table-column label="工况瞬时(m³/h)" min-width="130" align="right">
              <template slot-scope="{ row }">{{ formatNum(row.workInstant) }}</template>
            </el-table-column>
            <el-table-column label="压力(MPa)" width="100" align="right">
              <template slot-scope="{ row }">{{ formatNum(row.pressure) }}</template>
            </el-table-column>
            <el-table-column label="温度(℃)" width="90" align="right">
              <template slot-scope="{ row }">{{ formatNum(row.temperature) }}</template>
            </el-table-column>
          </template>

          <el-table-column label="状态" width="96" align="center">
            <template slot-scope="{ row }"><gas-status-tag kind="reportRecord" :value="row.recordStatus" /></template>
          </el-table-column>
        </el-table>
      </div>

      <div class="report-detail-footer" :class="{ 'is-pager-hidden': !showPager }">
        <el-pagination
          v-if="showPager"
          class="pager"
          background
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          :current-page="query.pageNum"
          :page-size="query.pageSize"
          :page-sizes="pageSizes"
          @size-change="$emit('size-change', $event)"
          @current-change="$emit('current-change', $event)"
        />
        <el-button @click="drawerVisible = false">关闭</el-button>
      </div>
    </div>
  </el-drawer>
</template>

<script>
import { formatMetricColumnLabel, metricValueFromRow } from '@/config/deviceTypeMetrics'

const METRIC_SUMMARY_FIELDS = {
  stdTotal: 'stdTotalSum',
  workTotal: 'workTotalSum',
  stdInstant: 'stdInstantAvg',
  workInstant: 'workInstantAvg',
  pressure: 'pressureAvg',
  temperature: 'temperatureAvg',
  inletPressure: 'inletPressureAvg',
  outletPressure: 'outletPressureAvg',
  leakage: 'leakageAvg',
  signalStrength: 'signalStrengthAvg',
  batteryLevel: 'batteryLevelAvg'
}

export default {
  name: 'ReportPeriodDetailDrawer',
  props: {
    visible: { type: Boolean, default: false },
    title: { type: String, default: '报表详情' },
    mode: { type: String, default: 'monthly' },
    detail: { type: Object, default: null },
    rows: { type: Array, default: () => [] },
    metrics: { type: Array, default: () => [] },
    showSubDevice: { type: Boolean, default: true },
    total: { type: Number, default: 0 },
    query: { type: Object, default: () => ({ pageNum: 1, pageSize: 31 }) },
    pageSizes: { type: Array, default: () => [10, 20, 31] },
    showPager: { type: Boolean, default: true },
    loading: { type: Boolean, default: false },
    rowsLoading: { type: Boolean, default: false },
    tableTitle: { type: String, default: '明细数据' },
    exportLabel: { type: String, default: '导出明细数据' }
  },
  computed: {
    drawerVisible: {
      get() {
        return this.visible
      },
      set(value) {
        this.$emit('update:visible', value)
      }
    },
    typedMetrics() {
      return Array.isArray(this.metrics) ? this.metrics : []
    },
    hasSampleTime() {
      return (this.rows || []).some(r => r && r.sampleTime)
    },
    tableHeight() {
      const h = typeof window !== 'undefined' ? window.innerHeight : 900
      return Math.max(280, h - 360)
    },
    summaryItems() {
      const d = this.detail || {}
      const periodLabel = d.periodLabel || this.rangeText(d.beginDate, d.endDate) || '-'
      const items = [
        { label: '统计区间', value: periodLabel },
        { label: '所属区域', value: d.regionName || '-' },
        { label: this.typedMetrics.length ? '设备类型' : '站点类型', value: (this.typedMetrics.length ? d.deviceType : d.siteType) || '-' },
        { label: '设备名称', value: this.reportDeviceName(d) || '-' }
      ]
      if (this.showSubDevice) {
        items.push({ label: '子设备', value: d.subDeviceLabel || d._subLabel || '—' })
      }
      if (!this.typedMetrics.length) {
        items.push({ label: '报表类型', value: this.reportTypeLabel(d.reportType) })
      }
      items.push({ label: '状态', value: d.recordStatus, kind: 'status' })
      if (this.mode === 'yearly' && !this.typedMetrics.length) {
        items.push(
          { label: '覆盖月份', value: d.monthCount || 0 },
          { label: '上报天数', value: d.dayCount || 0 },
          { label: '年标况累积', value: `${this.formatNum(d.stdTotalSum)} m³` },
          { label: '年均瞬时', value: `${this.formatNum(d.stdInstantAvg)} m³/h` },
          { label: '年工况累积', value: `${this.formatNum(d.workTotalSum)} m³` },
          { label: '异常天数', value: d.abnormalDays || 0 }
        )
        return items
      }
      if (this.mode !== 'yearly') items.push({ label: '上报天数', value: d.dayCount || 0 })
      if (this.typedMetrics.length) {
        this.typedMetrics.forEach(metric => {
          const unit = metric.unit ? ` ${metric.unit}` : ''
          items.push({
            label: metric.label,
            value: `${this.formatNum(this.summaryMetricValue(d, metric))}${unit}`
          })
        })
        return items
      }
      items.push(
        { label: '标况累积', value: `${this.formatNum(d.stdTotalSum)} m³` },
        { label: '均瞬时', value: `${this.formatNum(d.stdInstantAvg)} m³/h` },
        { label: '工况累积', value: `${this.formatNum(d.workTotalSum)} m³` },
        { label: '均压力', value: `${this.formatNum(d.pressureAvg)} MPa` }
      )
      return items
    }
  },
  methods: {
    rowIndex(index) {
      if (!this.showPager) return index + 1
      const pageNum = (this.query && this.query.pageNum) || 1
      const pageSize = (this.query && this.query.pageSize) || 0
      return (pageNum - 1) * pageSize + index + 1
    },
    periodLabel(row) {
      if (!row) return '-'
      const st = row.sampleTime ? String(row.sampleTime) : ''
      const d = (row.reportDate || '').toString()
      if (!st) return d || '-'
      // 间隔采样模式：明细行 sampleTime 为 HH:mm:ss，主列表实体序列化可能带完整日期
      if (st.includes('-')) return st
      const dateOnly = d.includes(' ') ? d.split(' ')[0] : d
      return `${dateOnly} ${st}`
    },
    formatNum(value) {
      if (value === null || value === undefined || value === '') return '-'
      const n = Number(value)
      if (Number.isNaN(n)) return String(value)
      return n.toLocaleString(undefined, { maximumFractionDigits: 2 })
    },
    metricColumnLabel(metric) {
      return formatMetricColumnLabel(metric)
    },
    metricValue(row, metric) {
      return metricValueFromRow(row, metric)
    },
    summaryMetricValue(detail, metric) {
      if (!detail || !metric) return null
      const field = METRIC_SUMMARY_FIELDS[metric.key]
      if (field && detail[field] != null && detail[field] !== '') return detail[field]
      return this.metricValue(detail, metric)
    },
    reportTypeLabel(value) {
      if (value === 'regulator') return '调压设备报表'
      if (value === 'flow_meter') return '流量计报表'
      return value || '-'
    },
    reportDeviceName(row) {
      if (!row) return ''
      return row.deviceName || row.siteName || row.deviceCode || row.terminalCode || '-'
    },
    rangeText(beginDate, endDate) {
      if (!beginDate && !endDate) return ''
      return `${beginDate || '-'} ~ ${endDate || '-'}`
    }
  }
}
</script>

<style scoped>
.report-detail-body {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
}
.report-detail-scroll {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 0 20px 12px;
  box-sizing: border-box;
}
.report-detail-info {
  margin-bottom: 16px;
}
.report-detail-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}
.table-title {
  font-size: 15px;
  font-weight: 600;
  color: #1d2129;
}
.table-title em {
  font-style: normal;
  font-weight: 400;
  color: #86909c;
  font-size: 13px;
  margin-left: 8px;
}
.report-detail-table >>> .report-detail-table-header {
  background: #4e5969 !important;
  color: #fff;
  font-weight: 500;
}
.report-detail-table >>> th.report-detail-table-header .cell {
  color: #fff;
}
.report-detail-footer {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 20px;
  border-top: 1px solid #e5e6eb;
  background: #fff;
}
.report-detail-footer.is-pager-hidden {
  justify-content: flex-end;
}
.report-detail-footer .pager {
  margin: 0;
  text-align: left;
}
</style>
